import { IStoreSetup } from "../typings/interfaces/responses/store-setup-response";
import { setStoreBranding } from "./store-branding";

// The store header printed on receipts and reports. Comes from Admin →
// Store Setup once it has been saved; until then the tenant's defaults.

export interface IStoreHeader {
  name: string;
  subtitle: string;
  /** One line: street, unit, city, state zip */
  address: string;
  /** (703) 998-6464 */
  phone: string;
  email: string;
  website: string;
}

export const DEFAULT_STORE_HEADER: IStoreHeader = {
  name: "Presto Valet",
  subtitle: "GreenEarth Dry Cleaning",
  address: "1623 N Quaker Ln, Alexandria, VA 22302",
  phone: "(703) 998-6464",
  email: "presto@prestovalet.net",
  website: "www.prestovalet.net",
};

let saved: IStoreSetup | null = null;

export const setStoreSetup = (setup?: IStoreSetup | null) => {
  saved = setup?.configured ? setup : null;
  setStoreBranding(
    saved && {
      primaryColor: saved.primaryColor,
      secondaryColor: saved.secondaryColor,
      logo: saved.logo,
      dateFormat: saved.dateFormat,
      timeFormat: saved.timeFormat,
    }
  );
};

/** 703-998-6464 → (703) 998-6464; anything else as typed */
export const displayPhone = (phone?: string | null) => {
  const digits = (phone ?? "").replace(/\D/g, "");
  return digits.length === 10
    ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    : (phone ?? "").trim();
};

export const formatStoreAddress = (setup: Pick<IStoreSetup, "address" | "unit" | "city" | "state" | "zip">) => {
  const street = [setup.address, setup.unit].map((p) => p?.trim()).filter(Boolean).join(" ");
  const stateZip = [setup.state, setup.zip].map((p) => p?.trim()).filter(Boolean).join(" ");
  return [street, setup.city?.trim(), stateZip].filter(Boolean).join(", ");
};

/** Saved Store Setup values, each falling back to the given defaults when blank. */
export const getStoreHeader = (fallback: Partial<IStoreHeader> = {}): IStoreHeader => {
  const base = { ...DEFAULT_STORE_HEADER, ...dropBlank(fallback) };
  if (!saved) return base;
  return {
    name: saved.storeName?.trim() || base.name,
    subtitle: saved.slogan?.trim() || base.subtitle,
    address: formatStoreAddress(saved) || base.address,
    phone: displayPhone(saved.phone) || base.phone,
    email: saved.email?.trim() || base.email,
    website: saved.website?.trim() || base.website,
  };
};

/** The store fields of a ticket/receipt print payload. */
export const receiptStoreFields = (fallback: Partial<IStoreHeader> = {}) => {
  const header = getStoreHeader(fallback);
  return {
    storeName: header.name,
    storeSubtitle: header.subtitle,
    storeAddress: header.address,
    storePhone: header.phone,
    storeWebsite: header.website,
  };
};

const dropBlank = (header: Partial<IStoreHeader>) =>
  Object.fromEntries(Object.entries(header).filter(([, v]) => typeof v === "string" && v.trim())) as Partial<IStoreHeader>;
